// Every page address of the app, written once (plan: docs/superpowers/plans/2026-11-04-page-links.md).
// The API (links in notifications, emails, Home, the calendar) and the web app
// (links between pages) build page links with pageLink() and pageUrl(), never by
// hand, so moving a page is a change to one line here.
//
// The patterns are the router's own (":projectId", ":id" ...); the web router's
// screen list (apps/web/src/routes/coverage.ts) must use the same addresses, and
// apps/web/src/routes/__tests__/pages-coverage.test.ts checks that it does.
// Not here: the API's own addresses (/api/...) and the government area (/org),
// which is switched off for now.

export const PAGES = {
  // Project: Home, search, setup, import, settings
  projectHome: "/p/:projectId",
  activityLog: "/p/:projectId/activity",
  importConfirm: "/p/:projectId/import/confirm",
  importDone: "/p/:projectId/import/done",
  importGaps: "/p/:projectId/import/gaps",
  importReview: "/p/:projectId/import/review",
  importTakeOver: "/p/:projectId/import/take-over",
  search: "/p/:projectId/search",
  settingsPartners: "/p/:projectId/settings/partners",
  settingsPartner: "/p/:projectId/settings/partners/:id",
  settingsSetup: "/p/:projectId/settings/setup",
  setupBranding: "/p/:projectId/setup/branding",
  setupContract: "/p/:projectId/setup/contract",
  setupDone: "/p/:projectId/setup/done",
  setupLogframe: "/p/:projectId/setup/logframe",
  setupTeam: "/p/:projectId/setup/team",
  setupTorTemplate: "/p/:projectId/setup/tor-template",
  // Activities and events
  // R2-012 (Simon's second run, D6): the section's address is its Overview (the sidebar's first tab);
  // the list moved to /activities/all. The Overview sends an older list link (?type=, ?status=, ?period=,
  // ?q=) on to the list, so those keep working.
  activities: "/p/:projectId/activities",
  activitiesAll: "/p/:projectId/activities/all",
  activity: "/p/:projectId/activities/:id",
  activityApprove: "/p/:projectId/activities/:id/approve",
  activityCheckIn: "/p/:projectId/activities/:id/check-in",
  activityCheckInWalkIn: "/p/:projectId/activities/:id/check-in/walk-in",
  activityEdit: "/p/:projectId/activities/:id/edit",
  activityEmails: "/p/:projectId/activities/:id/emails",
  activityFeedback: "/p/:projectId/activities/:id/feedback",
  activityFeedbackForm: "/p/:projectId/activities/:id/feedback-form",
  activityFollowUp: "/p/:projectId/activities/:id/follow-up",
  activityInvitationsEmail: "/p/:projectId/activities/:id/invitations/email",
  activityInvitationsEmailPreview: "/p/:projectId/activities/:id/invitations/email/preview",
  activityLogistics: "/p/:projectId/activities/:id/logistics",
  activityParticipants: "/p/:projectId/activities/:id/participants",
  activityParticipantSpeaker: "/p/:projectId/activities/:id/participants/:registrationId/speaker",
  activityParticipantsExport: "/p/:projectId/activities/:id/participants/export",
  activityProgramme: "/p/:projectId/activities/:id/programme",
  activityProgrammePreview: "/p/:projectId/activities/:id/programme/preview",
  activityRegistrations: "/p/:projectId/activities/:id/registrations",
  activityReport: "/p/:projectId/activities/:id/report",
  activityRequestChanges: "/p/:projectId/activities/:id/request-changes",
  activityRsvp: "/p/:projectId/activities/:id/rsvp",
  activityRsvpQuestion: "/p/:projectId/activities/:id/rsvp/questions/:questionId",
  activityRsvpQuestionsNew: "/p/:projectId/activities/:id/rsvp/questions/new",
  activitySpeakers: "/p/:projectId/activities/:id/speakers",
  activitySpeakersPreview: "/p/:projectId/activities/:id/speakers/preview",
  activitiesCalendar: "/p/:projectId/activities/calendar/:month",
  activitiesImport: "/p/:projectId/activities/import",
  activitiesMeetings: "/p/:projectId/activities/meetings",
  activitiesMeeting: "/p/:projectId/activities/meetings/:id",
  activitiesMeetingMinutes: "/p/:projectId/activities/meetings/:id/minutes",
  activitiesMeetingPapers: "/p/:projectId/activities/meetings/:id/papers",
  activitiesMeetingsNew: "/p/:projectId/activities/meetings/new",
  activitiesNew: "/p/:projectId/activities/new",
  // Experts
  experts: "/p/:projectId/experts",
  expert: "/p/:projectId/experts/:id",
  expertAssign: "/p/:projectId/experts/:id/assign",
  expertEdit: "/p/:projectId/experts/:id/edit",
  expertPreview: "/p/:projectId/experts/:id/preview",
  expertTimesheet: "/p/:projectId/experts/:id/timesheets/:month",
  expertTimesheetLine: "/p/:projectId/experts/:id/timesheets/:month/lines/:line",
  expertsAssignments: "/p/:projectId/experts/assignments",
  expertsImport: "/p/:projectId/experts/import",
  expertsNew: "/p/:projectId/experts/new",
  expertsSelection: "/p/:projectId/experts/selection",
  expertNeed: "/p/:projectId/experts/selection/:id",
  expertNeedCandidate: "/p/:projectId/experts/selection/:id/candidates/:c",
  expertNeedContract: "/p/:projectId/experts/selection/:id/contract",
  expertNeedRequirements: "/p/:projectId/experts/selection/:id/requirements",
  expertsSelectionNew: "/p/:projectId/experts/selection/new",
  expertsTimesheets: "/p/:projectId/experts/timesheets",
  expertsWorkload: "/p/:projectId/experts/workload",
  // Stakeholders
  stakeholders: "/p/:projectId/stakeholders",
  stakeholder: "/p/:projectId/stakeholders/:id",
  stakeholderEdit: "/p/:projectId/stakeholders/:id/edit",
  stakeholdersDuplicates: "/p/:projectId/stakeholders/duplicates",
  stakeholdersImport: "/p/:projectId/stakeholders/import",
  stakeholdersInvite: "/p/:projectId/stakeholders/invite",
  stakeholdersLists: "/p/:projectId/stakeholders/lists",
  stakeholdersList: "/p/:projectId/stakeholders/lists/:id",
  stakeholdersListEdit: "/p/:projectId/stakeholders/lists/:id/edit",
  stakeholdersListsNew: "/p/:projectId/stakeholders/lists/new",
  stakeholdersNew: "/p/:projectId/stakeholders/new",
  stakeholdersOrganisations: "/p/:projectId/stakeholders/organisations",
  stakeholdersOrganisation: "/p/:projectId/stakeholders/organisations/:id",
  stakeholdersPrivacy: "/p/:projectId/stakeholders/privacy",
  stakeholdersPrivacyRequest: "/p/:projectId/stakeholders/privacy/requests/:id",
  stakeholdersPrivacyRequestsNew: "/p/:projectId/stakeholders/privacy/requests/new",
  // Contract & finance, Results & reporting (Marius's split of Administration and reporting, 4 Nov 2026)
  adminOverview: "/p/:projectId/admin",
  amendments: "/p/:projectId/admin/amendments",
  amendment: "/p/:projectId/admin/amendments/:id",
  amendmentDecision: "/p/:projectId/admin/amendments/:id/decision",
  amendmentsNew: "/p/:projectId/admin/amendments/new",
  approvals: "/p/:projectId/admin/approvals",
  audit: "/p/:projectId/admin/audit",
  auditPack: "/p/:projectId/admin/audit/pack",
  budget: "/p/:projectId/admin/budget",
  budgetImport: "/p/:projectId/admin/budget/import",
  budgetTransfer: "/p/:projectId/admin/budget/transfer",
  closeProject: "/p/:projectId/admin/close",
  contract: "/p/:projectId/admin/contract",
  contractCompare: "/p/:projectId/admin/contract/compare",
  contractEdit: "/p/:projectId/admin/contract/edit",
  documents: "/p/:projectId/admin/documents",
  documentsNew: "/p/:projectId/admin/documents/new",
  expenses: "/p/:projectId/admin/expenses",
  expense: "/p/:projectId/admin/expenses/:id",
  expenseEdit: "/p/:projectId/admin/expenses/:id/edit",
  expensesNew: "/p/:projectId/admin/expenses/new",
  expertDays: "/p/:projectId/admin/expert-days",
  financialReport: "/p/:projectId/admin/financial-report",
  fromCm: "/p/:projectId/admin/from-cm",
  funderView: "/p/:projectId/admin/funder-view",
  adminHistory: "/p/:projectId/admin/history/:entityType/:entityId",
  howItWorks: "/p/:projectId/admin/how-it-works",
  invoices: "/p/:projectId/admin/invoices",
  invoicesNew: "/p/:projectId/admin/invoices/new",
  logframe: "/p/:projectId/admin/logframe",
  logframeEdit: "/p/:projectId/admin/logframe/edit",
  logframeImport: "/p/:projectId/admin/logframe/import",
  logframeReadingsNew: "/p/:projectId/admin/logframe/readings/new",
  logframeTable: "/p/:projectId/admin/logframe/table",
  monthly: "/p/:projectId/admin/monthly",
  monthlyReport: "/p/:projectId/admin/monthly/:month",
  reports: "/p/:projectId/admin/reports",
  report: "/p/:projectId/admin/reports/:id",
  reportDecision: "/p/:projectId/admin/reports/:id/decision",
  reportPlanned: "/p/:projectId/admin/reports/:id/planned",
  reportPreview: "/p/:projectId/admin/reports/:id/preview",
  reportSubmit: "/p/:projectId/admin/reports/:id/submit",
  reportWrite: "/p/:projectId/admin/reports/:id/write",
  reportsFinal: "/p/:projectId/admin/reports/final",
  reportsSaved: "/p/:projectId/admin/reports/saved",
  adminSetup: "/p/:projectId/admin/setup",
  tors: "/p/:projectId/admin/tors",
  tor: "/p/:projectId/admin/tors/:id",
  torApproval: "/p/:projectId/admin/tors/:id/approval",
  torChanges: "/p/:projectId/admin/tors/:id/changes",
  torEdit: "/p/:projectId/admin/tors/:id/edit",
  torExport: "/p/:projectId/admin/tors/:id/export",
  torVersion: "/p/:projectId/admin/tors/:id/versions/:version",
  torsNew: "/p/:projectId/admin/tors/new",
  analytics: "/p/:projectId/analytics",
  analyticsExport: "/p/:projectId/analytics/export",
  analyticsPeople: "/p/:projectId/analytics/people",
  reportingOverview: "/p/:projectId/reporting",
  // Projects and onboarding
  projects: "/projects",
  projectsImportCall: "/projects/import/call",
  projectsNew: "/projects/new",
  projectsNewDocuments: "/projects/new/:projectId/documents",
  projectsNewReading: "/projects/new/:projectId/reading",
  projectsNewReady: "/projects/new/:projectId/ready",
  projectsNewReview: "/projects/new/:projectId/review/:section",
  // Account, sign-in, organisation settings, staff
  forgotPassword: "/forgot-password",
  help: "/help",
  joinTeam: "/join/:token",
  notifications: "/notifications",
  resetPassword: "/reset-password",
  settingsAccount: "/settings/account",
  settingsBilling: "/settings/billing",
  settingsNotifications: "/settings/notifications",
  settingsOrganisation: "/settings/organisation",
  settingsOutbox: "/settings/outbox",
  settingsTags: "/settings/tags",
  settingsTeam: "/settings/team",
  settingsTeamRole: "/settings/team/:id/role",
  settingsTeamInvite: "/settings/team/invite",
  signIn: "/sign-in",
  signIn2fa: "/sign-in/2fa",
  signIn2faSetup: "/sign-in/2fa/setup",
  signInDone: "/sign-in/done",
  signInExpert: "/sign-in/expert",
  signInLink: "/sign-in/link",
  signInLinkOpen: "/sign-in/link/:token",
  signInSso: "/sign-in/sso",
  signInTeam: "/sign-in/team",
  signOut: "/sign-out",
  signUpExpert: "/sign-up/expert",
  signUpTeam: "/sign-up/team",
  staffEmails: "/staff/emails",
  staffOrganisationsNew: "/staff/organisations/new",
  staffTrials: "/staff/trials",
  verifyEmail: "/verify-email",
  welcome: "/welcome",
  welcomeAccount: "/welcome/account",
  welcomeDone: "/welcome/done",
  welcomeHouseStyle: "/welcome/house-style",
  welcomeLibrary: "/welcome/library",
  welcomeOrganisation: "/welcome/organisation",
  welcomePayment: "/welcome/payment",
  welcomeTeam: "/welcome/team",
  // Expert portal
  portal: "/portal",
  portalActivityReport: "/portal/activities/:id/report",
  portalClaimsNew: "/portal/claims/new",
  portalContracts: "/portal/contracts",
  portalCv: "/portal/cv",
  portalDetails: "/portal/details",
  portalHelp: "/portal/help",
  portalHours: "/portal/hours",
  portalInvoices: "/portal/invoices",
  portalMessagesNew: "/portal/messages/new",
  portalTimesheet: "/portal/timesheets/:month",
  // Contract manager
  cmStart: "/cm",
  cmProject: "/cm/:projectId",
  cmActivities: "/cm/:projectId/activities",
  cmActivity: "/cm/:projectId/activities/:activityId",
  cmBudget: "/cm/:projectId/budget",
  cmDocuments: "/cm/:projectId/documents",
  cmIndicators: "/cm/:projectId/indicators",
  cmMonthly: "/cm/:projectId/monthly",
  cmMonthlyReport: "/cm/:projectId/monthly/:month",
  cmReports: "/cm/:projectId/reports",
  cmTors: "/cm/:projectId/tors",
  cmTor: "/cm/:projectId/tors/:torId",
  cmJoin: "/cm/join/:token",
  cmSignIn: "/cm/sign-in",
  // Guest, token and print pages
  publicForm: "/f/:token",
  feedbackPreview: "/feedback/:token",
  invite: "/invite/:token",
  inviteConfirmed: "/invite/:token/confirmed",
  printTor: "/print/tor/:id",
  privacyConfirm: "/privacy/confirm/:token",
  privacyRequest: "/privacy/request",
  rsvp: "/r/:slug",
  rsvpConfirm: "/r/confirm/:token",
  checkInRoom: "/room/:token",
  funderShare: "/share/:token",
  speaker: "/speaker/:token",
  yourInformation: "/your-information/:token",
} as const;

export type PageName = keyof typeof PAGES;

/** The ":name" parts of a pattern, as a union of names. */
type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

/** The ids a page needs: pageLink("tor", { projectId, id }). */
export type PageParams<K extends PageName> = { [N in ParamNames<(typeof PAGES)[K]>]: string };

/** What can follow the address: a query (?a=1) and a fragment (#line-3). */
export type PageExtras = {
  query?: Record<string, string | number | boolean | null | undefined> | URLSearchParams | string;
  hash?: string;
};

type PageArgs<K extends PageName> = [ParamNames<(typeof PAGES)[K]>] extends [never]
  ? [params?: Record<string, never>, extras?: PageExtras]
  : [params: PageParams<K>, extras?: PageExtras];

function queryString(query: PageExtras["query"]): string {
  if (query === undefined) return "";
  if (typeof query === "string") return query.replace(/^\?/, "");
  if (query instanceof URLSearchParams) return query.toString();
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) q.set(k, String(v));
  return q.toString();
}

/**
 * The address of a page inside the app, from its name and ids:
 * pageLink("tor", { projectId: "p1", id: "t1" }) is "/p/p1/admin/tors/t1".
 * Ids are URL-encoded; a query and a fragment can follow.
 */
export function pageLink<K extends PageName>(page: K, ...[params, extras]: PageArgs<K>): string {
  const values = (params ?? {}) as Record<string, string>;
  const path = PAGES[page].replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => encodeURIComponent(values[name] ?? ""));
  const q = queryString(extras?.query);
  const hash = extras?.hash ? `#${extras.hash.replace(/^#/, "")}` : "";
  return `${path}${q ? `?${q}` : ""}${hash}`;
}

/** The same address with the app's own address in front, for emails: pageUrl("https://app.convenor.eu", "tor", {...}). */
export function pageUrl<K extends PageName>(appUrl: string, page: K, ...args: PageArgs<K>): string {
  return `${appUrl.replace(/\/+$/, "")}${pageLink(page, ...args)}`;
}
